import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';

import { ContentPlatformImage } from '@/components/content-platform-image/src/content-platform-image';
import { CustomerStory } from '@/content/customerStories/customerStories.types';
import { expectContent } from '@/content/utils/expectContent';
import { Link } from '@/libraries/design/components/link/src/link';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';

// eslint-disable-next-line complexity
export const CustomerStoryCard = ({
	title,
	urlSlug,
	customerCompany,
	advocateQuotes,
	storyIcon,
	callToAction,
}: // eslint-disable-next-line complexity
CustomerStory) => {
	const href = useLocalizedHref(callToAction?.[0]?.url ?? `/customers/${urlSlug}`);
	const isExternalHref = href.startsWith('https://');

	const advocateQuote = advocateQuotes?.[0];
	const isQuote = !!advocateQuote?.advocateHeadshot?.url;

	return (
		<a
			href={href}
			target={isExternalHref ? '_blank' : undefined}
			rel={isExternalHref ? 'noreferrer noopener' : undefined}
			css={customerStoryCardsStyles}
		>
			{customerCompany &&
				(customerCompany?.logo?.[0]?.url ? (
					<div css={customerLogoStyles}>
						<ContentPlatformImage
							alt={customerCompany.name ?? customerCompany.logo[0].description ?? ' '}
							src={customerCompany.logo[0].url}
							contentType={customerCompany.logo[0].contentType}
							baseWidth={200}
							height="40px"
						/>
					</div>
				) : (
					<div css={customerLogoStyles}>
						<p>{expectContent(customerCompany.name)}</p>
					</div>
				))}
			{storyIcon?.url && !isQuote && (
				<div css={spotIllustrationStyles}>
					<ContentPlatformImage
						alt={' ' /* we consider these illustrations decorative */}
						src={storyIcon.url}
						contentType={storyIcon.contentType}
						baseWidth={300}
					/>
				</div>
			)}
			<div css={storyTitleStyles}>
				{advocateQuote?.advocateHeadshot?.url ? (
					<>
						<ContentPlatformImage
							alt={' ' /* details here would duplicate information elsewhere on the card */}
							src={advocateQuote.advocateHeadshot.url}
							contentType={advocateQuote.advocateHeadshot.contentType}
							baseWidth={400}
						/>
						<div>
							<h4>{advocateQuote.advocateName}</h4>
							<p className="eyebrow-md">{advocateQuote.advocateJobTitle}</p>
						</div>
						<blockquote>
							<p className="body-lg">{advocateQuote.advocateQuote}</p>
						</blockquote>
					</>
				) : (
					<p className="body-lg">{title}</p>
				)}
			</div>
			{callToAction && callToAction[0] && callToAction[0].text && (
				// eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name
				<Link
					className="customer-story-link-container"
					href={href}
					text={callToAction[0].text}
					type="primary"
					isStyleOnly
				/>
			)}
		</a>
	);
};

const customerStoryCardsStyles = css({
	[media.below.md]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.customer-story-link-container': {
			opacity: '1',
		},
	},
	'&:hover': {
		boxShadow: '0px 8px 12px 0px #091E4226, 0px 0px 1px 0px #091E424F',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.customer-story-link-container': {
			opacity: '1',
		},
	},
	padding: '24px 24px 32px 24px',
	backgroundColor: '#FFFFFF',
	borderRadius: '20px',
	boxShadow: '0px 1px 1px 0px #091E4240, 0px 0px 1px 0px #091E424F',
	transition: 'box-shadow ease-in-out 200ms',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.customer-story-link-container': {
		opacity: '0',
		transition: 'ease-in-out 0.2s opacity',
	},
});

const customerLogoStyles = css({
	marginBlockEnd: '24px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	img: {
		maxWidth: '55%',
		height: '40px',
		objectFit: 'contain',
	},
});
const spotIllustrationStyles = css({
	marginBlockEnd: '24px',
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	img: {
		width: '100%',
		maxWidth: '10rem',
		marginInline: 'auto',
		aspectRatio: '1',
	},
});

const storyTitleStyles = css({
	display: 'grid',
	gap: '1.5rem',
	margin: '0 0 24px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h4: {
		margin: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	img: {
		width: '100%',
		maxWidth: '16rem',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	p: {
		margin: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	blockquote: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'p:before': {
			content: 'open-quote',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'p:after': {
			content: 'close-quote',
		},
		margin: 0,
	},
});
